import React, { useMemo } from 'react';
import { FormDataType, Modal } from '@tw/tw-component-library';
import { SectionKey, sectionKeyToDisplay } from '../../../types';
import { StyledComponents } from './FormStack.styles';
import { PaymentWorkflowType } from '../../../components/container/forms/types/api/graph.types';
import { getOvertimeSectionFlow } from '../../../components/container/forms/schemas/OvertimeApproval/utils';
import {
  getGiftHospitalitySectionFlow,
  isGiftHospitalityWorkflow,
} from '../../../components/container/forms/schemas/GiftHospitalityDonation/authoriserRules';

const { CompletionModalContent } = StyledComponents;

export interface SectionCompletionModalProps {
  isOpen: boolean;
  completedDate?: string;
  onClose?: () => void;
  onGoToDashboard: () => void;
  currentSection: SectionKey;
  sectionProgress: Record<
    SectionKey,
    { isComplete: boolean; completedDate?: string }
  >;
  isRejected: boolean;
  submittedSections: SectionKey[];
  sectionFlow: SectionKey[];
  formData?: FormDataType;
  workflowType?: PaymentWorkflowType;
}

// Helper function to check if a value has data (not empty array, object, or string)
const hasData = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return Boolean(value);
};

// Helper function to check if second_partner fields have data (Dubai Payment Approval)
const hasSecondPartnerData = (formData?: FormDataType): boolean => {
  if (!formData) return false;

  const secondPartner = formData.second_partner;
  const secondPartnerAbove25k = formData.second_partner_above_25k;
  const secondPartnerBelow25k = formData.second_partner_below_25k;

  return (
    hasData(secondPartner) ||
    hasData(secondPartnerAbove25k) ||
    hasData(secondPartnerBelow25k)
  );
};

// Transfer Requisition: second partner only applies for Client → Client transfers
const isClientToClientTransfer = (formData?: FormDataType): boolean => {
  if (!formData) return false;
  return formData.debitType === 'client' && formData.creditType === 'client';
};

export const SectionCompletionModal = React.memo(
  ({
    isOpen,
    completedDate,
    onGoToDashboard,
    currentSection,
    sectionProgress,
    isRejected,
    submittedSections,
    sectionFlow,
    formData,
    workflowType,
  }: SectionCompletionModalProps) => {
    const isCurrentSectionSubmitted =
      submittedSections.includes(currentSection);
    const isCurrentSectionRejected = isRejected && isCurrentSectionSubmitted;

    // Filter section flow for conditional sections:
    // - Dubai Payment Approval: exclude second_partner when no second partner data
    // - Transfer Requisition: exclude second partner unless Client → Client
    // - payment_hold_review: only when resp_hold_payment is 'yes'
    const filteredSectionFlow = useMemo(() => {
      let flow = [...sectionFlow];

      if (
        workflowType === PaymentWorkflowType.DubaiPaymentApproval &&
        !hasSecondPartnerData(formData)
      ) {
        flow = flow.filter((section) => section !== 'second_partner');
      }

      if (
        workflowType === PaymentWorkflowType.DubaiTransferRequisition &&
        !isClientToClientTransfer(formData)
      ) {
        flow = flow.filter(
          (section) => section !== 'dubai_transfer_requisition_second_partner'
        );
      }

      // Include payment_hold_review only when the payment is on hold
      if (formData?.resp_hold_payment === 'yes') {
        if (!flow.includes('payment_hold_review')) {
          const paymentConfirmationIndex = flow.indexOf('payment_confirmation');
          if (paymentConfirmationIndex !== -1) {
            flow.splice(paymentConfirmationIndex, 0, 'payment_hold_review');
          } else {
            flow.push('payment_hold_review');
          }
        }
      } else {
        flow = flow.filter((section) => section !== 'payment_hold_review');
      }

      if (workflowType === PaymentWorkflowType.OvertimeApproval) {
        flow = getOvertimeSectionFlow(formData) as SectionKey[];
      }

      if (isGiftHospitalityWorkflow(workflowType)) {
        flow = getGiftHospitalitySectionFlow(flow, formData) as SectionKey[];
      }

      return flow;
    }, [sectionFlow, formData, workflowType]);

    return (
      <Modal
        isOpen={isOpen}
        onClose={onGoToDashboard}
        title={
          isCurrentSectionRejected ? 'Section Rejected' : 'Section Complete'
        }
        size="medium"
      >
        <CompletionModalContent>
          <div className="completion-modal-content">
            <h3>
              {isCurrentSectionRejected
                ? 'Section has been rejected!'
                : 'Section completed successfully!'}
            </h3>
            <p>
              <strong>
                {sectionKeyToDisplay[currentSection] ?? currentSection}
              </strong>{' '}
              {isCurrentSectionRejected
                ? 'has been rejected.'
                : 'has been completed.'}
            </p>
            <p>
              {isCurrentSectionRejected ? 'Rejected on:' : 'Completed on:'}{' '}
              {completedDate ? new Date(completedDate).toLocaleString() : ''}
            </p>

            <div className="completion-summary">
              <h4>Section Progress:</h4>
              {filteredSectionFlow.map((section) => {
                const isComplete =
                  sectionProgress[section]?.isComplete || false;
                const isSectionSubmitted = submittedSections.includes(section);
                const isSectionRejected = isRejected && isSectionSubmitted;

                return (
                  <div key={section} className="section-status">
                    <span>{sectionKeyToDisplay[section] ?? section}</span>
                    <span>
                      {isSectionRejected ? '❌' : isComplete ? '✅' : '⏳'}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="action-buttons">
              <button onClick={onGoToDashboard}>Go Back to Dashboard</button>
            </div>
          </div>
        </CompletionModalContent>
      </Modal>
    );
  }
);
